import React, { useCallback, useMemo, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { updateEdiInventoryAdj } from '../../services/ediinventoryadj.service';

/**
 * Bespoke edit drawer for EDI Inventory Adjustments (cms.edi_inventoryadj). All of the
 * transaction data is read-only — the page only lets users mark a row sent/unsent
 * (Sent Date, column edisentdatetime) or deleted. Unlike EDI Events there is no archive
 * table, so every row is editable this way. Replaces V1 editedi_inventoryadj.
 */
const TABLE = 'edi-inventory-adj';
const s = (v: any): string => (v == null ? '' : String(v));

const Field: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col">
    <span className="text-xs font-medium text-muted mb-1">{label}</span>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
      {(children == null || children === '') ? <span className="text-gray-400">—</span> : children}
    </div>
  </div>
);

const EdiInventoryAdjEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const recno = row ? Number(row.recno) : 0;

  // datetime-local wants "YYYY-MM-DDTHH:MM"; the row's edisentdatetime is "YYYY-MM-DD HH:MM".
  const [sent, setSent] = useState(s(row?.edisentdatetime).replace(' ', 'T'));
  const [formDeleted, setFormDeleted] = useState(s(row?.deleted) === 'Y');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const dirty = useMemo(
    () => sent !== s(row?.edisentdatetime).replace(' ', 'T') || formDeleted !== (s(row?.deleted) === 'Y'),
    [sent, formDeleted, row],
  );

  const handleSave = useCallback(async () => {
    if (!dirty) { onClose(); return; }
    setSaveError(null);
    setIsSaving(true);
    try {
      await updateEdiInventoryAdj(recno, { edisentdatetime: sent, deleted: formDeleted ? 'Y' : 'N' });
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to update EDI inventory adjustment.');
      setIsSaving(false);
    }
  }, [dirty, recno, sent, formDeleted, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> Save Changes</>)}
      </button>
    </div>
  );

  const title = `EDI Inventory Adjustment — Pro #${s(row?.pronumber)}`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="46rem" footer={footer}>
      <div className="space-y-5">
        <div ref={topRef} />
        {!!recno && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}

        {saveError && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
            {saveError}
          </div>
        )}

        {/* Transaction data — always read-only. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Pro Number">{s(row?.pronumber)}</Field>
          <Field label="Rcv Recno">{s(row?.rcvrecno)}</Field>
          <Field label="Rcv Units Recno">{s(row?.rcvunitsrecno)}</Field>
          <Field label="Rcv Unit Info Recno">{s(row?.rcvunitinforecno)}</Field>
          <Field label="Movement Type">{s(row?.movementtype)}</Field>
          <Field label="Transfer Reason">{s(row?.transferreason)}</Field>
          <Field label="Product">{s(row?.product)}</Field>
          <Field label="New Product">{s(row?.newproduct)}</Field>
          <Field label="Batch No.">{s(row?.batchnumber)}</Field>
          <Field label="New Batch No.">{s(row?.newbatchnumber)}</Field>
          <Field label="Tax Status">{s(row?.taxstatus)}</Field>
          <Field label="New Tax Status">{s(row?.newtaxstatus)}</Field>
          <Field label="Stock Status">{s(row?.stockstatus)}</Field>
          <Field label="Qty">{s(row?.qty)}</Field>
          <Field label="Uom">{s(row?.uom)}</Field>
          <Field label="Event Date">{s(row?.eventdatetime)}</Field>
          <Field label="Sent ID">{s(row?.edisentidnumber)}</Field>
        </div>

        {/* Sent Date + Deleted — the only editable fields. */}
        <div>
          <label className="block text-sm font-medium text-label mb-1">Sent Date</label>
          <div className="flex items-center gap-2">
            <input type="datetime-local" value={sent} disabled={isSaving}
              onChange={(e) => setSent(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
            {sent && (
              <button type="button" onClick={() => setSent('')} disabled={isSaving}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                Mark unsent
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-muted">
            Set a Sent Date to mark the adjustment sent, or clear it to mark it unsent.
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <label className="flex items-start gap-3 cursor-pointer select-none py-1">
            <input type="checkbox" checked={formDeleted} disabled={isSaving}
              onChange={(e) => setFormDeleted(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
            <span className="text-sm font-medium text-label">Deleted</span>
          </label>
          <p className="mt-1 ml-7 text-xs text-muted">
            Marks this adjustment deleted — hidden from filters but preserved in history. Uncheck and save to restore.
          </p>
        </div>
      </div>
    </Drawer>
  );
};

export default EdiInventoryAdjEditDrawer;
